import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { listQuotes, QuoteApiError } from '@/lib/quotationService';
import type { CustomerQuoteListItem } from '@/lib/quotationTypes';

const statusColors: Record<string, string> = {
  priced: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  final_offer_requested: 'bg-amber-50 text-amber-700 border-amber-200',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
  superseded: 'bg-ink-50 text-ink-400 border-ink-200',
};

const statusLabels: Record<string, string> = {
  priced: '견적 완료',
  final_offer_requested: '본사 검토중',
  cancelled: '취소됨',
  superseded: '대체됨',
};

export default function DashboardQuotes() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<CustomerQuoteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchQuotes() {
      setLoading(true);
      setError(null);
      try {
        const data = await listQuotes(1, 5);
        if (cancelled) return;
        setQuotes(data.items);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof QuoteApiError) {
          setError(err.message);
        } else {
          setError('견적 목록을 불러오는 중 오류가 발생했습니다.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchQuotes();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="rounded-xl border border-ink-100 bg-white">
      <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border-b border-ink-100">
        <div className="flex items-center gap-2">
          <i className="ri-file-list-3-line text-warm-400 text-lg" />
          <h3 className="text-sm md:text-base font-semibold text-ink-600">{t('dashboard.recentQuotes.title')}</h3>
        </div>
        <button
          onClick={() => navigate('/my/quotes')}
          className="text-xs md:text-sm font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap cursor-pointer"
        >
          {t('dashboard.seeAll')}
        </button>
      </div>

      {loading && (
        <div className="px-4 md:px-5 py-6 flex items-center justify-center gap-2">
          <div className="w-5 h-5 border-2 border-ink-200 border-t-ink-500 rounded-full animate-spin" />
          <p className="text-xs text-ink-400">불러오는 중...</p>
        </div>
      )}

      {!loading && error && (
        <div className="px-4 md:px-5 py-6 text-center">
          <p className="text-xs text-rose-500">{error}</p>
          <button
            onClick={() => setLoading(true)}
            className="mt-2 text-xs text-warm-400 underline cursor-pointer"
          >
            다시 시도
          </button>
        </div>
      )}

      {!loading && !error && quotes.length === 0 && (
        <div className="px-4 md:px-5 py-8 text-center">
          <div className="w-12 h-12 rounded-full bg-cream-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-file-list-3-line text-cream-400 text-xl" />
          </div>
          <p className="text-sm text-ink-300">{t('dashboard.recentQuotes.empty')}</p>
        </div>
      )}

      {!loading && !error && quotes.length > 0 && (
        <div className="divide-y divide-ink-100">
          {quotes.map((q) => {
            const statusCls = statusColors[q.status] || 'bg-ink-50 text-ink-400 border-ink-200';
            const statusLabel = statusLabels[q.status] || q.status;
            const dp = q.currency === 'KRW' ? 0 : 2;
            const totalNum = Number(q.quoteTotal);
            const formattedTotal = Number.isNaN(totalNum)
              ? q.quoteTotal
              : totalNum.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });

            return (
              <div key={q.quoteId} className="px-4 md:px-5 py-3 md:py-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono text-warm-400">{q.quoteNumber}</span>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${statusCls}`}>
                        {statusLabel}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-ink-300 flex-wrap">
                      <span>제품 {q.productCount}개</span>
                      <span>{new Date(q.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 sm:gap-1 shrink-0">
                    <span className="text-sm font-semibold text-ink-600">
                      {q.currency} {formattedTotal}
                    </span>
                    <button
                      onClick={() => navigate(`/my/quotes/${q.quoteId}`)}
                      className="text-xs font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {t('dashboard.recentQuotes.view')} →
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 px-4 md:px-5 py-3 border-t border-ink-100">
        <button
          onClick={() => navigate('/my/quotes')}
          className="flex-1 text-center text-xs md:text-sm font-medium px-3 py-2 rounded-lg border border-ink-200 text-ink-500 hover:bg-cream-100 transition-colors whitespace-nowrap cursor-pointer"
        >
          {t('dashboard.recentQuotes.viewAll')}
        </button>
        <button
          onClick={() => navigate('/packaging')}
          className="flex-1 text-center text-xs md:text-sm font-medium px-3 py-2 rounded-lg bg-warm-500 text-white hover:bg-warm-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          {t('dashboard.recentQuotes.requestNew')}
        </button>
      </div>
    </div>
  );
}